import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PhrenosWordmark } from '../components/phrenos-brand';
import { colors } from '../components/phrenos-brand';

interface LandingPageProps {
  onSignIn: () => void;
}

// ── Inline SVG icons for feature cards ──────────────────────────────────────

const IconFlashcard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round">
    <rect x="2" y="4" width="20" height="16" rx="3" /><line x1="2" y1="9" x2="22" y2="9" />
  </svg>
);
const IconSynonym = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round">
    <path d="M8 12h8M8 8h8M8 16h5" />
  </svg>
);
const IconFast = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconQuant = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />
  </svg>
);
const IconAI = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h2a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1a7 7 0 0 1-7 7H9a7 7 0 0 1-7-7H1a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h2V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2z" />
  </svg>
);
const IconHard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 2a10 10 0 1 0 0 20" /><path d="M12 6v6l4 2" />
  </svg>
);
const IconSync = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const FEATURES = [
  { Icon: IconFlashcard, title: 'Flashcards',       desc: '1100+ words with multiple choice. Instant feedback. Review wrong answers in one tap.' },
  { Icon: IconSynonym,   title: 'Synonym matching', desc: 'Select every synonym for each word. Deepens recall beyond simple definitions.' },
  { Icon: IconFast,      title: 'Fast Attack',      desc: '30-day spaced repetition schedule. New words + rotating review groups every day.' },
  { Icon: IconQuant,     title: 'Quant tracker',    desc: 'Log problems with timer, error type, and difficulty. See patterns in your mistakes.' },
  { Icon: IconAI,        title: 'AI problem solver', desc: 'Snap a photo of any quant problem you got wrong. Get a plain-English explanation from an AI tutor instantly.' },
  { Icon: IconHard,      title: 'Hard Terms',       desc: 'Curated list of the toughest high-frequency words. Separate drill mode.' },
  { Icon: IconSync,      title: 'Progress sync',    desc: 'Sign in to save progress across devices. Study time tracked per mode.' },
];

const STEPS = [
  { title: 'Pick your exam',            desc: 'GRE, SAT, GMAT, LSAT or MCAT — the word bank and quant modes adapt to what you\'re studying for.' },
  { title: 'Choose a study mode',       desc: 'Start with Flashcards for breadth, switch to Hard Terms for depth, use Fast Attack for a 30-day structured plan.' },
  { title: 'Track what you get wrong',  desc: 'Every incorrect answer is saved. Review Wrong mode drills your weak spots until they stick.' },
  { title: 'Log your quant practice',   desc: 'Time every problem, tag error types, spot patterns. Know exactly where your quant score is leaking.' },
  { title: 'Get AI explanations',        desc: 'Snap any problem you got wrong. An AI tutor explains the solution in plain English — what went wrong, why the answer is right, and what to watch for next time.' },
];

const EXAMS = [
  { name: 'GRE®',  desc: 'Verbal reasoning & quant. 1100+ high-frequency words.' },
  { name: 'SAT®',  desc: 'Evidence-based reading vocab and math problem tracking.' },
  { name: 'GMAT®', desc: 'Critical reasoning vocab and quant error logging.' },
  { name: 'LSAT®', desc: 'Logical reasoning vocabulary and reading comprehension words.' },
];

// ── Shared style helpers ─────────────────────────────────────────────────────

const ff = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const pillBtn: React.CSSProperties = {
  background: colors.primary, color: '#fff', border: 'none',
  borderRadius: 999, padding: '9px 20px', fontSize: 13, fontWeight: 700,
  cursor: 'pointer', fontFamily: ff,
};

const sectionPad: React.CSSProperties = { padding: '40px 24px' };

// ── Component ────────────────────────────────────────────────────────────────

export default function LandingPage({ onSignIn }: LandingPageProps) {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Phrenos?',
        acceptedAnswer: { '@type': 'Answer', text: 'Phrenos is a focused GRE, SAT, and GMAT prep app with 1,100+ vocabulary flashcards, synonym matching, hard terms quizzes, spaced repetition (Fast Attack), and a quant problem tracker.' },
      },
      {
        '@type': 'Question',
        name: 'Is Phrenos free?',
        acceptedAnswer: { '@type': 'Answer', text: 'Phrenos offers a free trial with access to all features. After the trial, a Pro subscription unlocks everything including all 1,100+ words, unlimited sessions, and cross-device sync.' },
      },
      {
        '@type': 'Question',
        name: 'Which exams does Phrenos cover?',
        acceptedAnswer: { '@type': 'Answer', text: 'Phrenos covers GRE, SAT, and GMAT vocabulary and quantitative preparation.' },
      },
      {
        '@type': 'Question',
        name: 'Is Phrenos good for students with ADHD?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Phrenos is designed with ADHD-friendly study in mind — short focused sessions, instant feedback, spaced repetition, and no overwhelming walls of text. It is used by students with ADHD preparing for the GRE, GMAT, and MBA programs.' },
      },
      {
        '@type': 'Question',
        name: 'Is there a GRE study app that works for ADHD?',
        acceptedAnswer: { '@type': 'Answer', text: 'Phrenos is a GRE study app built for focus and distraction-free learning. Its ADHD-friendly design uses spaced repetition, chunked progress, and short daily sessions to help students with ADHD retain vocabulary and quant concepts effectively.' },
      },
      {
        '@type': 'Question',
        name: 'Does Phrenos have an AI tutor?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Phrenos Pro includes an AI problem solver powered by Claude. Take a photo of any GRE, GMAT, or SAT quant problem you got wrong and the AI tutor explains the correct answer step by step in plain English — including the key concept and common traps to avoid.' },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Phrenos – GRE &amp; GMAT Study App | AI Tutor, Flashcards &amp; Quant Prep</title>
        <meta name="description" content="The GRE and GMAT study app with AI-powered problem explanations. Snap a wrong answer, get a plain-English walkthrough. Plus 1,100+ vocab flashcards, spaced repetition, and quant tracking. Free to start." />
        <link rel="canonical" href="https://phrenos.app/" />
        <meta property="og:title" content="Phrenos – GRE & GMAT Study App | AI Tutor, Flashcards & Quant Prep" />
        <meta property="og:description" content="The GRE and GMAT study app with AI-powered problem explanations. Snap a wrong answer, get a plain-English walkthrough. Plus 1,100+ vocab flashcards, spaced repetition, and quant tracking. Free to start." />
        <meta property="og:url" content="https://phrenos.app/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
    <div style={{ fontFamily: ff, backgroundColor: colors.background, color: colors.textPrimary, overflowX: 'hidden' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* ── 1. NAV ── */}
        <nav aria-label="Site navigation" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', height: 56, background: colors.background,
          borderBottom: `1px solid ${colors.border}`,
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <PhrenosWordmark iconSize={28} variant="default" />
          <button style={pillBtn} onClick={onSignIn}>Start studying free</button>
        </nav>

        {/* ── 2. HERO ── */}
        <section style={{
          background: colors.primary, padding: '56px 24px 48px',
          textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: '#2a6356', opacity: 0.35, top: -150, left: -100, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: '#2a6356', opacity: 0.2, bottom: -80, right: -60, pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#2d6454', color: colors.mintText,
              borderRadius: 999, padding: '5px 14px',
              fontSize: 11, fontWeight: 600, marginBottom: 20, letterSpacing: '0.3px',
            }}>
              Study sharper.
            </div>

            <h1 style={{
              fontSize: 'clamp(32px, 7vw, 48px)', fontWeight: 800, color: '#fff',
              lineHeight: 1.15, letterSpacing: '-1px', margin: '0 0 14px',
            }}>
              Vocab &amp; quant prep<br />
              <span style={{ color: colors.mintText }}>built for focus</span>
            </h1>

            <p style={{
              fontSize: 15, color: colors.mintText, lineHeight: 1.6,
              maxWidth: 400, margin: '0 auto 28px',
            }}>
              The GRE study app built for ADHD brains. Flashcards, synonym drills, spaced repetition, and quant tracking — short sessions, instant feedback, no overwhelm. 1100+ words.
            </p>

            <button
              onClick={onSignIn}
              style={{
                background: '#fff', color: colors.primary,
                borderRadius: 999, padding: '15px 40px',
                fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
                fontFamily: ff, display: 'inline-block',
              }}
            >
              Start studying free
            </button>
            <p style={{ fontSize: 11, color: colors.confirmation, marginTop: 10 }}>
              No credit card. Works on any device.
            </p>

            {/* Exam pills */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 28 }}>
              {['GRE®', 'SAT®', 'GMAT®', 'LSAT®', 'MCAT®'].map(e => (
                <span key={e} style={{
                  background: '#2d6454', color: colors.mintText,
                  borderRadius: 999, padding: '5px 14px',
                  fontSize: 12, fontWeight: 600,
                }}>{e}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ── 3. STATS BAR ── */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 1, background: colors.border,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          {[
            { num: '1100+', label: 'Vocab words' },
            { num: '5',     label: 'Study modes' },
            { num: '30',    label: 'Day program' },
          ].map(s => (
            <div key={s.label} style={{ background: colors.card, padding: '20px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: colors.primary }}>{s.num}</div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── 4. FEATURES ── */}
        <section style={sectionPad}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: colors.confirmation, marginBottom: 8 }}>Features</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: colors.primary, lineHeight: 1.2, marginBottom: 10, letterSpacing: '-0.5px' }}>
            Everything you need to prep smarter
          </h2>
          <p style={{ fontSize: 14, color: colors.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>
            Five focused modes that cover vocab, synonyms, hard terms, and quant — plus a built-in progress tracker so you always know where you stand.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {FEATURES.map(({ Icon, title, desc }) => (
              <div key={title} style={{
                background: colors.card, border: `1px solid ${colors.border}`,
                borderRadius: 16, padding: '18px 16px',
              }}>
                <div style={{
                  width: 36, height: 36, background: colors.successLight,
                  borderRadius: 10, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', marginBottom: 10, flexShrink: 0,
                }}>
                  <Icon />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: colors.primary, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 5. APP PREVIEW ── */}
        <section style={{ ...sectionPad, paddingTop: 0 }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: colors.confirmation, marginBottom: 8 }}>See it in action</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: colors.primary, lineHeight: 1.2, marginBottom: 16, letterSpacing: '-0.5px' }}>
            Clean. Fast. Distraction-free.
          </h2>
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 18, padding: '20px 18px' }}>
            <div style={{
              display: 'inline-block', background: colors.primary, color: '#fff',
              borderRadius: 999, padding: '8px 24px', fontSize: 20, fontWeight: 700, marginBottom: 14,
            }}>abate</div>
            <p style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 12 }}>What does this word mean?</p>
            {[
              { letter: 'A', text: 'Open and responsive to suggestion.',         state: 'default' },
              { letter: 'B', text: 'To lessen in intensity or amount; to subside.', state: 'correct' },
              { letter: 'C', text: 'A consequence or outgrowth.',                state: 'wrong'    },
              { letter: 'D', text: 'To eliminate completely; to root out.',       state: 'default' },
            ].map(({ letter, text, state }) => (
              <div key={letter} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 13px', border: `1px solid ${
                  state === 'correct' ? colors.confirmation :
                  state === 'wrong'   ? '#E57373' :
                  colors.border}`,
                borderRadius: 10, marginBottom: 7, fontSize: 12,
                background: state === 'correct' ? colors.successLight :
                            state === 'wrong'   ? '#FDECEA' : '#fff',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  border: `1.5px solid ${state === 'correct' ? colors.confirmation : state === 'wrong' ? '#E57373' : colors.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 600,
                  background: state === 'correct' ? colors.confirmation : state === 'wrong' ? '#E57373' : 'transparent',
                  color: state !== 'default' ? '#fff' : colors.textSecondary,
                }}>{letter}</div>
                {text}
              </div>
            ))}
            {/* Progress bar */}
            <div style={{ height: 4, background: colors.border, borderRadius: 999, overflow: 'hidden', marginTop: 12 }}>
              <div style={{ height: '100%', background: colors.primary, borderRadius: 999, width: '30%' }} />
            </div>
          </div>
        </section>

        {/* ── 6. HOW IT WORKS ── */}
        <section style={{
          ...sectionPad, background: colors.card,
          borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`,
        }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: colors.confirmation, marginBottom: 8 }}>How it works</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: colors.primary, lineHeight: 1.2, marginBottom: 24, letterSpacing: '-0.5px' }}>
            Go from zero to test-ready
          </h2>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {STEPS.map((step, i) => (
              <li key={i} style={{
                display: 'flex', gap: 16, padding: '16px 0',
                borderBottom: i < STEPS.length - 1 ? `1px solid ${colors.border}` : 'none',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: colors.primary, color: '#fff',
                  fontSize: 13, fontWeight: 700, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2,
                }}>{i + 1}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.primary, marginBottom: 3 }}>{step.title}</div>
                  <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ── 7. EXAM COVERAGE ── */}
        <section style={sectionPad}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: colors.confirmation, marginBottom: 8 }}>Exam coverage</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: colors.primary, lineHeight: 1.2, marginBottom: 10, letterSpacing: '-0.5px' }}>
            Built for every standardized test
          </h2>
          <p style={{ fontSize: 14, color: colors.textSecondary, lineHeight: 1.7, marginBottom: 20 }}>
            Phrenos covers the high-frequency vocabulary and quantitative concepts that show up across all major graduate and undergraduate admissions exams.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {EXAMS.map(exam => (
              <div key={exam.name} style={{
                background: colors.card, border: `1px solid ${colors.border}`,
                borderRadius: 14, padding: 16,
              }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: colors.primary, marginBottom: 3 }}>{exam.name}</div>
                <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.5 }}>{exam.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 8. ADHD / FOCUS ── */}
        <section style={{ ...sectionPad, paddingTop: 0 }}>
          <div style={{ background: colors.primary, borderRadius: 18, padding: '24px 20px' }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 8, letterSpacing: '-0.4px' }}>
              The ADHD-friendly GRE &amp; GMAT study app
            </h2>
            <p style={{ fontSize: 13, color: colors.mintText, lineHeight: 1.7, marginBottom: 16 }}>
              Phrenos is built around how distracted brains actually learn — short sessions, instant feedback, chunked progress, and no overwhelming walls of text. Perfect for GRE and GMAT prep with ADHD.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['ADHD-friendly', 'Short sessions', 'Instant feedback', 'No overwhelm', 'Spaced repetition'].map(tag => (
                <span key={tag} style={{
                  background: '#2d6454', color: colors.mintText,
                  borderRadius: 999, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                }}>{tag}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. FINAL CTA ── */}
        <section style={{
          background: colors.card, borderTop: `1px solid ${colors.border}`,
          padding: '40px 24px', textAlign: 'center',
        }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: colors.primary, marginBottom: 8, letterSpacing: '-0.5px' }}>
            Start studying sharper today
          </h2>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 24, lineHeight: 1.6 }}>
            Free to use. No credit card. Works on any device.<br />
            Sign in to sync progress across all your devices.
          </p>
          <button
            onClick={onSignIn}
            style={{
              background: colors.primary, color: '#fff',
              borderRadius: 999, padding: '15px 44px',
              fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer',
              fontFamily: ff, display: 'inline-block',
            }}
          >
            Open Phrenos free
          </button>
          <p style={{ fontSize: 10, color: colors.textSecondary, marginTop: 12, lineHeight: 1.6 }}>
            GRE® is a registered trademark of ETS. Phrenos is not affiliated with or endorsed by ETS.
          </p>
        </section>

        {/* ── 10. FOOTER ── */}
        <footer style={{
          padding: '20px 24px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: colors.primary, fontFamily: ff }}>phrenos</span>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <a href="/privacy" style={{ fontSize: 11, color: colors.textSecondary, textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/terms" style={{ fontSize: 11, color: colors.textSecondary, textDecoration: 'none' }}>Terms of Service</a>
          </div>
          <p style={{ fontSize: 10, color: colors.textSecondary, lineHeight: 1.6, maxWidth: 400, margin: 0 }}>
            GRE®, SAT®, GMAT®, LSAT®, MCAT® are registered trademarks of their respective owners. Phrenos is not affiliated with or endorsed by any of these organizations.
          </p>
        </footer>

      </div>
    </div>
    </>
  );
}
